import React, { useRef, useEffect, useState, useCallback } from "react";
import { gsap } from "gsap";
import { useRouter } from "next/navigation";
import { LucideIcon } from "lucide-react";

export interface BentoCardProps {
  color?: string;
  title?: string;
  description?: string;
  label?: string;
  symbol?: string;
  textAutoHide?: boolean;
  disableAnimations?: boolean;
  href?: string;
  icon?: LucideIcon;
  iconSrc?: string;
  glowColor?: string;
}

export interface BentoProps {
  navItems?: BentoCardProps[];
  textAutoHide?: boolean;
  enableStars?: boolean;
  enableSpotlight?: boolean;
  enableBorderGlow?: boolean;
  disableAnimations?: boolean;
  spotlightRadius?: number;
  particleCount?: number;
  enableTilt?: boolean;
  glowColor?: string;
  clickEffect?: boolean;
  enableMagnetism?: boolean;
  width?: string;
  height?: string;
}

const DEFAULT_PARTICLE_COUNT = 12;
const DEFAULT_SPOTLIGHT_RADIUS = 300;
const DEFAULT_GLOW_COLOR = "132, 0, 255";
const MOBILE_BREAKPOINT = 768;

const defaultCardData: BentoCardProps[] = [
  {
    color: "#060010",
    title: "Analytics",
    description: "Track user behavior",
    label: "Insights",
  },
  {
    color: "#060010",
    title: "Dashboard",
    description: "Centralized data view",
    label: "Overview",
  },
  {
    color: "#060010",
    title: "Collaboration",
    description: "Work together seamlessly",
    label: "Teamwork",
  },
  {
    color: "#060010",
    title: "Automation",
    description: "Streamline workflows",
    label: "Efficiency",
  },
  {
    color: "#060010",
    title: "Integration",
    description: "Connect favorite tools",
    label: "Connectivity",
  },
  {
    color: "#060010",
    title: "Security",
    description: "Enterprise-grade protection",
    label: "Protection",
  },
];

const createParticleElement = (
  x: number,
  y: number,
  color: string = DEFAULT_GLOW_COLOR,
): HTMLDivElement => {
  const el = document.createElement("div");
  el.className = "particle";
  el.style.cssText = `
    position: absolute;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: rgba(${color}, 1);
    box-shadow: 0 0 6px rgba(${color}, 0.6);
    pointer-events: none;
    z-index: 100;
    left: ${x}px;
    top: ${y}px;
  `;
  return el;
};

const calculateSpotlightValues = (radius: number) => ({
  proximity: radius * 0.5,
  fadeDistance: radius * 0.75,
});

const MatrixCanvas: React.FC<{
  isActive: boolean;
  glowColor?: string;
  containerRef: React.RefObject<HTMLDivElement | null>;
}> = ({ isActive, glowColor = DEFAULT_GLOW_COLOR, containerRef }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const frameCountRef = useRef(0);

  useEffect(() => {
    if (!isActive || !canvasRef.current || !containerRef.current) return;

    const canvas = canvasRef.current;
    const container = containerRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resizeCanvas = () => {
      const rect = container.getBoundingClientRect();
      canvas.width = rect.width - 20;
      canvas.height = rect.height;
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    const fontSize = 16;
    const columns = Math.floor(canvas.width + 16 / fontSize);
    const drops: number[] = new Array(columns).fill(1);
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789$@#%";
    const frameSkip = 3;

    ctx.font = `${fontSize}px monospace`;

    const matrix = () => {
      frameCountRef.current++;

      if (frameCountRef.current % frameSkip === 0) {
        ctx.fillStyle = "rgba(6, 0, 16, 0.05)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = `rgba(${glowColor}, 0.8)`;
        ctx.textAlign = "center";

        for (let i = 0; i < drops.length; i++) {
          const text = chars[Math.floor(Math.random() * chars.length)];
          ctx.fillText(text, i * fontSize, drops[i] * fontSize);
          if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
            drops[i] = 0;
          }
          drops[i]++;
        }
      }

      animationRef.current = requestAnimationFrame(matrix);
    };

    matrix();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      frameCountRef.current = 0;
      window.removeEventListener("resize", resizeCanvas);
    };
  }, [isActive, glowColor, containerRef]);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute inset-0 h-full w-full opacity-0 transition-opacity duration-300 group-hover:opacity-30"
      style={{ zIndex: 0 }}
    />
  );
};

const updateCardGlowProperties = (
  card: HTMLElement,
  mouseX: number,
  mouseY: number,
  glow: number,
  radius: number,
) => {
  const rect = card.getBoundingClientRect();
  const relativeX = ((mouseX - rect.left) / rect.width) * 100;
  const relativeY = ((mouseY - rect.top) / rect.height) * 100;

  card.style.setProperty("--glow-x", `${relativeX}%`);
  card.style.setProperty("--glow-y", `${relativeY}%`);
  card.style.setProperty("--glow-intensity", glow.toString());
  card.style.setProperty("--glow-radius", `${radius}px`);
};

const ParticleCard: React.FC<{
  children: React.ReactNode;
  className?: string;
  disableAnimations?: boolean;
  style?: React.CSSProperties;
  particleCount?: number;
  glowColor?: string;
  enableTilt?: boolean;
  clickEffect?: boolean;
  enableMagnetism?: boolean;
  onClick?: () => void;
}> = ({
  children,
  className = "w-fit h-fit",
  disableAnimations = false,
  style,
  particleCount = DEFAULT_PARTICLE_COUNT,
  glowColor = DEFAULT_GLOW_COLOR,
  enableTilt = true,
  clickEffect = false,
  enableMagnetism = false,
  onClick,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const particlesRef = useRef<HTMLDivElement[]>([]);
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);
  const isHoveredRef = useRef(false);
  const memoizedParticles = useRef<HTMLDivElement[]>([]);
  const particlesInitialized = useRef(false);
  const magnetismAnimationRef = useRef<gsap.core.Tween | null>(null);
  const [isMatrixActive, setIsMatrixActive] = useState(false);

  const initializeParticles = useCallback(() => {
    if (particlesInitialized.current || !cardRef.current) return;

    const { width, height } = cardRef.current.getBoundingClientRect();
    memoizedParticles.current = Array.from({ length: particleCount }, () =>
      createParticleElement(
        Math.random() * width,
        Math.random() * height,
        glowColor,
      ),
    );
    particlesInitialized.current = true;
  }, [particleCount, glowColor]);

  const clearAllParticles = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    magnetismAnimationRef.current?.kill();

    particlesRef.current.forEach((particle) => {
      gsap.to(particle, {
        scale: 0,
        opacity: 0,
        duration: 0.3,
        ease: "back.in(1.7)",
        onComplete: () => {
          particle.parentNode?.removeChild(particle);
        },
      });
    });
    particlesRef.current = [];
  }, []);

  const animateParticles = useCallback(() => {
    if (!cardRef.current || !isHoveredRef.current) return;

    if (!particlesInitialized.current) {
      initializeParticles();
    }

    memoizedParticles.current.forEach((particle, index) => {
      const timeoutId = setTimeout(() => {
        if (!isHoveredRef.current || !cardRef.current) return;

        const clone = particle.cloneNode(true) as HTMLDivElement;
        cardRef.current.appendChild(clone);
        particlesRef.current.push(clone);

        gsap.fromTo(
          clone,
          { scale: 0, opacity: 0 },
          {
            scale: 1,
            opacity: 1,
            duration: 0.3,
            ease: "back.out(1.7)",
          },
        );

        const randomDelay = Math.random() * 1000;
        setTimeout(() => {
          if (!isHoveredRef.current || !cardRef.current) return;

          const rect = cardRef.current.getBoundingClientRect();
          gsap.to(clone, {
            x: `+=${(Math.random() - 0.5) * rect.width}`,
            y: `+=${(Math.random() - 0.5) * rect.height}`,
            opacity: 0,
            duration: 1 + Math.random(),
            ease: "power1.inOut",
            onComplete: () => {
              if (particle.parentNode === cardRef.current) {
                cardRef.current?.removeChild(clone);
              }
              const idx = particlesRef.current.indexOf(clone);
              if (idx > -1) particlesRef.current.splice(idx, 1);
            },
          });
        }, randomDelay);
      }, index * 100);

      timeoutsRef.current.push(timeoutId);
    });
  }, [initializeParticles]);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!cardRef.current || disableAnimations) return;

      const rect = cardRef.current.getBoundingClientRect();
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      if (enableTilt) {
        const angleY = ((mouseX - centerX) / centerX) * 15;
        const angleX = ((centerY - mouseY) / centerY) * 15;

        gsap.to(cardRef.current, {
          rotateX: angleX,
          rotateY: angleY,
          duration: 0.3,
          ease: "power2.out",
          transformPerspective: 1000,
        });
      }

      if (enableMagnetism) {
        const deltaX = ((mouseX - centerX) / centerX) * 10;
        const deltaY = ((mouseY - centerY) / centerY) * 10;

        magnetismAnimationRef.current?.kill();
        magnetismAnimationRef.current = gsap.to(cardRef.current, {
          x: deltaX,
          y: deltaY,
          duration: 0.3,
          ease: "power2.out",
        });
      }
    },
    [disableAnimations, enableTilt, enableMagnetism],
  );

  const handleMouseEnter = useCallback(() => {
    if (disableAnimations) return;
    isHoveredRef.current = true;
    setIsMatrixActive(true);
    animateParticles();
  }, [disableAnimations, animateParticles]);

  const handleMouseLeave = useCallback(() => {
    if (disableAnimations) return;
    isHoveredRef.current = false;
    setIsMatrixActive(false);
    clearAllParticles();

    if (cardRef.current) {
      if (enableTilt) {
        gsap.to(cardRef.current, {
          rotateX: 0,
          rotateY: 0,
          duration: 0.5,
          ease: "power2.out",
        });
      }

      if (enableMagnetism) {
        magnetismAnimationRef.current?.kill();
        magnetismAnimationRef.current = gsap.to(cardRef.current, {
          x: 0,
          y: 0,
          duration: 0.5,
          ease: "elastic.out(1, 0.5)",
        });
      }
    }
  }, [disableAnimations, clearAllParticles, enableTilt, enableMagnetism]);

  const handleClick = useCallback(() => {
    if (!clickEffect || !cardRef.current) return;

    gsap.to(cardRef.current, {
      scale: 0.95,
      duration: 0.1,
      yoyo: true,
      repeat: 1,
      ease: "power2.inOut",
    });
  }, [clickEffect]);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    card.addEventListener("mouseenter", handleMouseEnter);
    card.addEventListener("mouseleave", handleMouseLeave);
    card.addEventListener("mousemove", handleMouseMove);
    card.addEventListener("click", handleClick);

    return () => {
      card.removeEventListener("mouseenter", handleMouseEnter);
      card.removeEventListener("mouseleave", handleMouseLeave);
      card.removeEventListener("mousemove", handleMouseMove);
      card.removeEventListener("click", handleClick);
      clearAllParticles();
    };
  }, [
    handleMouseEnter,
    handleMouseLeave,
    handleMouseMove,
    handleClick,
    clearAllParticles,
  ]);

  return (
    <div
      ref={cardRef}
      className={`group relative ${className}`}
      style={{
        ...style,
        position: "relative",
        zIndex: 2,
        transition: "none",
      }}
      onClick={onClick}
    >
      <MatrixCanvas
        isActive={isMatrixActive}
        glowColor={glowColor}
        containerRef={cardRef}
      />
      {children}
    </div>
  );
};

const BasicCard: React.FC<{
  children: React.ReactNode;
  className?: string;
  disableAnimations?: boolean;
  style?: React.CSSProperties;
  onClick?: () => void;
}> = ({ children, className = "", style, onClick }) => (
  <div className={`group ${className}`} style={style} onClick={onClick}>
    {children}
  </div>
);

const GlobalSpotlight: React.FC<{
  gridRef: React.RefObject<HTMLDivElement | null>;
  disableAnimations?: boolean;
  enabled?: boolean;
  spotlightRadius?: number;
  glowColor?: string;
}> = ({
  gridRef,
  disableAnimations = false,
  enabled = true,
  spotlightRadius = DEFAULT_SPOTLIGHT_RADIUS,
  glowColor = DEFAULT_GLOW_COLOR,
}) => {
  const spotlightRef = useRef<HTMLDivElement | null>(null);
  const isInsideSection = useRef(false);

  useEffect(() => {
    if (disableAnimations || !gridRef?.current || !enabled) return;

    const spotlight = document.createElement("div");
    spotlight.className = "global-spotlight";
    spotlight.style.cssText = `
      position: fixed;
      width: 800px;
      height: 800px;
      border-radius: 50%;
      pointer-events: none;
      background: radial-gradient(circle,
        rgba(${glowColor}, 0.15) 0%,
        rgba(${glowColor}, 0.08) 15%,
        rgba(${glowColor}, 0.04) 25%,
        rgba(${glowColor}, 0.02) 40%,
        rgba(${glowColor}, 0.01) 65%,
        transparent 70%
      );
      z-index: 200;
      opacity: 0;
      transform: translate(-50%, -50%);
      mix-blend-mode: screen;
    `;
    document.body.appendChild(spotlight);
    spotlightRef.current = spotlight;

    const handleMouseMove = (e: MouseEvent) => {
      if (!spotlightRef.current || !gridRef.current) return;

      const section = gridRef.current.closest(".bento-section");
      const rect = section?.getBoundingClientRect();
      const mouseInside =
        rect &&
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom;

      isInsideSection.current = mouseInside || false;
      const cards = gridRef.current.querySelectorAll(".card");

      if (!mouseInside) {
        gsap.to(spotlightRef.current, {
          opacity: 0,
          duration: 0.3,
          ease: "power2.out",
        });
        cards.forEach((card) => {
          (card as HTMLElement).style.setProperty("--glow-intensity", "0");
        });
        return;
      }

      const { proximity, fadeDistance } =
        calculateSpotlightValues(spotlightRadius);
      let minDistance = Infinity;

      cards.forEach((card) => {
        const cardElement = card as HTMLElement;
        const cardRect = cardElement.getBoundingClientRect();
        const centerX = cardRect.left + cardRect.width / 2;
        const centerY = cardRect.top + cardRect.height / 2;
        const distance =
          Math.hypot(e.clientX - centerX, e.clientY - centerY) -
          Math.max(cardRect.width, cardRect.height) / 2;
        const effectiveDistance = Math.max(0, distance);

        minDistance = Math.min(minDistance, effectiveDistance);

        let glowIntensity = 0;
        if (effectiveDistance <= proximity) {
          glowIntensity = 1;
        } else if (effectiveDistance <= fadeDistance) {
          glowIntensity =
            (fadeDistance - effectiveDistance) / (fadeDistance - proximity);
        }

        updateCardGlowProperties(
          cardElement,
          e.clientX,
          e.clientY,
          glowIntensity,
          spotlightRadius,
        );
      });

      gsap.to(spotlightRef.current, {
        left: e.clientX,
        top: e.clientY,
        duration: 0.1,
        ease: "power2.out",
      });

      const targetOpacity =
        minDistance <= proximity
          ? 0.8
          : minDistance <= fadeDistance
            ? ((fadeDistance - minDistance) / (fadeDistance - proximity)) * 0.8
            : 0;

      gsap.to(spotlightRef.current, {
        opacity: targetOpacity,
        duration: targetOpacity > 0 ? 0.2 : 0.5,
        ease: "power2.out",
      });
    };

    const handleMouseLeave = () => {
      isInsideSection.current = false;
      gridRef.current?.querySelectorAll(".card").forEach((card) => {
        (card as HTMLElement).style.setProperty("--glow-intensity", "0");
      });
      if (spotlightRef.current) {
        gsap.to(spotlightRef.current, {
          opacity: 0,
          duration: 0.3,
          ease: "power2.out",
        });
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseleave", handleMouseLeave);
      spotlightRef.current?.parentNode?.removeChild(spotlightRef.current);
    };
  }, [gridRef, disableAnimations, enabled, spotlightRadius, glowColor]);

  return null;
};

const generateCardSymbol = (text: string): string => {
  if (!text) return "??";
  const words = text.split(/\s+/);
  if (words.length > 1) {
    return words
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }
  const camelMatch = text.match(/[A-Z]/g);
  if (camelMatch && camelMatch.length >= 2) {
    return camelMatch.slice(0, 2).join("");
  }
  return text.slice(0, 2).toUpperCase();
};

const CardContent: React.FC<{
  card: BentoCardProps;
  index: number;
  isHeroCard: boolean;
  textAutoHide: boolean;
  glowColor?: string;
}> = ({
  card,
  index,
  isHeroCard,
  textAutoHide,
  glowColor = DEFAULT_GLOW_COLOR,
}) => {
  const Icon = card.icon;

  // Use card's glowColor if available, otherwise use the global glowColor
  const effectiveGlowColor = card.glowColor || `rgb(${glowColor})`;

  return (
    <div className="relative w-full h-full flex flex-col">
      {/* Header with icon - absolute positioned */}
      <div className="absolute top-0 left-0 right-0 flex justify-end items-start p-2 z-50">
        {card.iconSrc ? (
          <img
            src={card.iconSrc}
            alt={card.label || card.title || "icon"}
            className={`${
              isHeroCard ? "h-16 w-16" : "h-12 w-12"
            } object-contain`}
            style={{
              filter: `drop-shadow(0 0 4px ${effectiveGlowColor})`,
            }}
          />
        ) : Icon ? (
          <Icon
            className={`${isHeroCard ? "h-16 w-16" : "h-12 w-12"}`}
            style={{ color: effectiveGlowColor }}
          />
        ) : null}
      </div>

      {/* Center content with symbol and label - absolutely centered */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div
          className={`card-symbol font-bold hover:drop-shadow-lg text-9xl mb-2 leading-none transition-all duration-300 ease-in-out group-hover:scale-125 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:rotate-1`}
          style={{
            fontFamily: "'Anta', sans-serif",
            fontWeight: 400,
            letterSpacing: "0",
            color: effectiveGlowColor,
            transform: "perspective(1000px)",
            position: "relative",
            zIndex: 100,
          }}
        >
          {card.symbol || generateCardSymbol(card.label || card.title || "")}
        </div>
        <h3
          className={`font-medium text-3xl opacity-70 text-center ${textAutoHide ? "text-clamp-1" : ""}`}
          data-tour-card={(card.label || card.title)
            ?.toLowerCase()
            .replace(/\s+/g, "-")}
          style={{
            fontFamily: "'Quicksand', 'Poppins', 'Comfortaa', sans-serif",
            fontWeight: 500,
          }}
        >
          {card.label || card.title}
        </h3>
      </div>

      {/* Footer with description - absolute positioned at bottom */}
      <div className="absolute bottom-0 left-0 right-0 text-center p-2">
        <p
          className={`text-[16px] leading-tight opacity-40 m-0 ${textAutoHide ? "text-clamp-1" : ""}`}
          style={{
            fontFamily: "'Quicksand', 'Poppins', 'Comfortaa', sans-serif",
            fontWeight: 400,
          }}
        >
          {card.description}
        </p>
      </div>
    </div>
  );
};

const useMobileDetection = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () =>
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
};

// Helper function to convert hex to RGB
const hexToRgb = (hex: string): string => {
  // Remove # if present
  hex = hex.replace(/^#/, "");

  // Parse hex values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `${r}, ${g}, ${b}`;
};

// Helper function to normalize color to RGB format
const normalizeColor = (color: string): string => {
  // If it's already in RGB format (contains comma), return as is
  if (color.includes(",")) {
    return color;
  }
  // If it's hex format (starts with #), convert to RGB
  if (color.startsWith("#")) {
    return hexToRgb(color);
  }
  // Otherwise assume it's RGB without commas and return as is
  return color;
};

const MagicBento: React.FC<BentoProps> = ({
  navItems,
  textAutoHide = true,
  enableStars = true,
  enableSpotlight = true,
  enableBorderGlow = true,
  disableAnimations = false,
  spotlightRadius = DEFAULT_SPOTLIGHT_RADIUS,
  particleCount = DEFAULT_PARTICLE_COUNT,
  enableTilt = false,
  glowColor = DEFAULT_GLOW_COLOR,
  clickEffect = true,
  enableMagnetism = true,
  width = "100%",
  height = "100vh",
}) => {
  const gridRef = useRef<HTMLDivElement | null>(null);
  const isMobile = useMobileDetection();
  const shouldDisableAnimations = disableAnimations || isMobile;
  const router = useRouter();
  const cardData = navItems || defaultCardData;

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Anta&family=Quicksand:wght@300;400;500;600;700&family=Poppins:wght@300;400;500;600;700&family=Comfortaa:wght@300;400;500;600;700&display=swap');
          
          ${cardData
            .map((card, index) => {
              const cardColor = normalizeColor(card.glowColor || glowColor);
              return `
              .card-${index}.card--border-glow::after {
                background: radial-gradient(var(--glow-radius) circle at var(--glow-x) var(--glow-y),
                    rgba(${cardColor}, calc(var(--glow-intensity) * 0.8)) 0%,
                    rgba(${cardColor}, calc(var(--glow-intensity) * 0.4)) 30%,
                    transparent 60%) !important;
              }
              
              .card-${index}.card--border-glow:hover {
                box-shadow: 0 4px 20px rgba(46, 24, 78, 0.4), 0 0 30px rgba(${cardColor}, 0.2) !important;
              }
            `;
            })
            .join("")}
          
          .bento-section {
            --glow-x: 50%;
            --glow-y: 50%;
            --glow-intensity: 0;
            --glow-radius: 200px;
            --glow-color: ${glowColor};
            --border-color: #2e4e4e;
            --background-dark: #060010;
            --white: hsl(0, 0%, 100%);
          }
          
          .card-responsive {
            display: grid;
            grid-template-columns: 1fr;
            width: 100%;
            height: 100%;
            padding: 0.75rem;
            gap: 0.75rem;
            box-sizing: border-box;
          }
          
          @media (min-width: 640px) {
            .card-responsive {
              grid-template-columns: repeat(2, 1fr);
              padding: 1rem;
              gap: 1rem;
            }
          }
          
          @media (min-width: 1024px) {
            .card-responsive {
              grid-template-columns: 1fr 1fr 1fr;
              grid-template-rows: 1fr 1fr;
              padding: 1.5rem;
              gap: 1.25rem;
              height: 100%;
            }
            
            /* mCore - wide left, row 1 */
            .card-responsive .card:nth-child(1) {
              grid-column: 1 / 3;
              grid-row: 1;
            }

            /* Connection Studio - spans both bottom columns */
            .card-responsive .card:nth-child(2) {
              grid-column: 1 / 3;
              grid-row: 2;
            }

            /* mSpace - tall right, spans both rows */
            .card-responsive .card:nth-child(3) {
              grid-column: 3;
              grid-row: 1 / 3;
            }
          }
          
          @media (min-width: 1440px) {
            .card-responsive {
              padding: 1.5rem;
            }
          }
          
          @media (min-width: 1920px) {
            .card-responsive {
              padding: 2rem;
              gap: 1.5rem;
            }
          }
          
          .card--border-glow::after {
            content: '';
            position: absolute;
            inset: 0;
            padding: 6px;
            background: radial-gradient(var(--glow-radius) circle at var(--glow-x) var(--glow-y),
                rgba(${glowColor}, calc(var(--glow-intensity) * 0.8)) 0%,
                rgba(${glowColor}, calc(var(--glow-intensity) * 0.4)) 30%,
                transparent 60%);
            border-radius: inherit;
            mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            mask-composite: subtract;
            -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            -webkit-mask-composite: xor;
            pointer-events: none;
            opacity: 1;
            transition: opacity 0.3s ease;
            z-index: 1;
          }
          
          .card--border-glow:hover {
            box-shadow: 0 4px 20px rgba(46, 24, 78, 0.4), 0 0 30px rgba(${glowColor}, 0.2);
          }
          
          .card-symbol {
            filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0));
            transform-style: preserve-3d;
          }
          
          .group:hover .card-symbol {
            filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.9)) 
                    drop-shadow(0 15px 30px rgba(0, 0, 0, 0.7)) 
                    drop-shadow(0 8px 16px rgba(0, 0, 0, 0.6));
            transform: perspective(1000px) rotateX(15deg) translateZ(30px);
          }
          
          .particle::before {
            content: '';
            position: absolute;
            top: -2px;
            left: -2px;
            right: -2px;
            bottom: -2px;
            background: rgba(${glowColor}, 0.2);
            border-radius: 50%;
            z-index: -1;
          }
          
          .particle-container:hover {
            box-shadow: 0 4px 20px rgba(46, 24, 78, 0.2), 0 0 30px rgba(${glowColor}, 0.2);
          }
          
          .card {
            height: 100%;
            min-height: 120px;
          }
          
          .text-clamp-1 {
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 1;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          
          @media (max-width: 639px) {
            .card-responsive .card {
              min-height: 150px;
            }
          }
          
          @media (min-width: 640px) and (max-width: 1023px) {
            .card-responsive .card {
              min-height: 160px;
            }
          }
          
          @media (min-width: 1024px) {
            .card-responsive {
              align-content: stretch;
            }
            .card-responsive .card {
              min-height: 0;
              height: 100%;
            }
          }
        `}
      </style>

      {enableSpotlight && (
        <GlobalSpotlight
          gridRef={gridRef}
          disableAnimations={shouldDisableAnimations}
          enabled={enableSpotlight}
          spotlightRadius={spotlightRadius}
          glowColor={glowColor}
        />
      )}

      <div
        className="bento-section w-full h-full flex items-stretch justify-stretch select-none"
        ref={gridRef}
        style={{
          backgroundColor: "transparent",
          width: width,
          height: height,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div className="card-responsive w-full h-full">
          {cardData.map((card, index) => {
            const isHeroCard = index === 0;
            const baseClassName = `card card-${index} relative flex flex-col ${
              isHeroCard ? "p-8" : "p-6"
            } rounded-[10px] border border-solid font-light overflow-hidden transition-all duration-300 ease-in-out hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)] ${
              enableBorderGlow ? "card--border-glow" : ""
            } ${card.href ? "cursor-pointer" : ""}`;

            const cardGlowColor = normalizeColor(card.glowColor || glowColor);

            const cardStyle = {
              backgroundColor: card.color || "var(--background-dark)",
              borderColor: "var(--border-color)",
              color: "var(--white)",
              height: "100%",
              minHeight: isHeroCard ? "auto" : "auto",
            } as React.CSSProperties;

            const handleCardClick = () => {
              if (card.href) {
                router.push(card.href);
              }
            };

            const CardWrapper = enableStars ? ParticleCard : BasicCard;

            return (
              <CardWrapper
                key={index}
                className={baseClassName}
                style={cardStyle}
                onClick={handleCardClick}
                disableAnimations={shouldDisableAnimations}
                particleCount={particleCount}
                glowColor={cardGlowColor}
                enableTilt={enableTilt}
                clickEffect={clickEffect}
                enableMagnetism={enableMagnetism}
              >
                <CardContent
                  card={card}
                  index={index}
                  isHeroCard={isHeroCard}
                  textAutoHide={textAutoHide}
                  glowColor={cardGlowColor}
                />
              </CardWrapper>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default MagicBento;
